<script setup>
import { ref } from 'vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import RadioInput from '@/Shared/RadioInput.vue'
import TabLayout from '@/Shared/TabLayout.vue'
import NotificationIcon from '@/Shared/Icon/NotificationIcon.vue'
import GeneralIcon from '@/Shared/Icon/GeneralIcon.vue'
import SecurityIcon from '@/Shared/Icon/SecurityIcon.vue'
import { useForm } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import { useConfirm } from '@/Composables/useConfirm'

const props = defineProps({
  notificationChannel: {
    type: String,
    default: 'telegram'
  },
  notificationChannelOptions: {
    type: Array,
    required: true
  }
})

const form = useForm({
  notification_channel: props.notificationChannel
})

const activeTab = ref('notifications')

const tabs = [
  { id: 'notifications', label: 'Notificaciones', icon: NotificationIcon },
  { id: 'general', label: 'General', icon: GeneralIcon },
  { id: 'security', label: 'Seguridad', icon: SecurityIcon }
]

const breadcrumbs = [{ label: 'Configuración' }]

async function submit() {
  if (
    await useConfirm(
      '¿Estás seguro de que quieres cambiar el canal de notificaciones? Esto necesita configuración adicional y puede provocar que no se envíen las notificaciones. Por favor, ponte en contacto con el administrador si no estás seguro de lo que estás haciendo.'
    )
  ) {
    form.post('/settings/notification-channel')
  }
}

const handleTabChange = (tabId) => {
  activeTab.value = tabId
}
</script>

<template>
  <TabLayout
    title="Configuración"
    :breadcrumbs="breadcrumbs"
    :tabs="tabs"
    default-active-tab="notifications"
    @tab-change="handleTabChange"
  >
    <template #default="{ activeTab: currentTab }">
      <!-- Panel de Notificaciones -->
      <div
        v-if="currentTab === 'notifications'"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <form @submit.prevent="submit">
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-xl font-medium text-gray-800">Configuración de notificaciones</h2>
            <p class="mt-1 text-sm text-gray-500">
              Gestiona cómo se envían las notificaciones a los usuarios.
            </p>
          </div>

          <div class="p-6 space-y-6">
            <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-md">
              <div class="flex">
                <div class="flex-shrink-0">
                  <Icon name="InformationCircle" class="h-5 w-5 text-blue-400" />
                </div>
                <div class="ml-3">
                  <p class="text-sm text-blue-700">
                    Este cambio necesita configuración adicional. Ponte en contacto con el
                    administrador si no estás seguro de lo qué estás haciendo.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <h3 class="text-base font-medium text-gray-800 mb-3">Canal de notificaciones</h3>
              <p class="text-sm text-gray-600 mb-4">
                Selecciona el canal por el que se enviarán todas las notificaciones a los usuarios.
              </p>
              <div class="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <RadioInput
                  v-model="form.notification_channel"
                  name="notification_channel"
                  :options="notificationChannelOptions"
                  :error="form.errors.notification_channel"
                />
              </div>
            </div>
          </div>

          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end items-center">
            <LoadingButton :loading="form.processing" class="btn-indigo" type="submit">
              Guardar cambios
            </LoadingButton>
          </div>
        </form>
      </div>

      <!-- Panel de Configuración General -->
      <div
        v-else-if="currentTab === 'general'"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-medium text-gray-800">Configuración general</h2>
          <p class="mt-1 text-sm text-gray-500">
            Gestiona las configuraciones generales de la aplicación.
          </p>
        </div>

        <div class="p-6 space-y-6">
          <div class="text-center py-8">
            <GeneralIcon class="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 class="text-lg font-medium text-gray-800 mb-2">Configuración en desarrollo</h3>
            <p class="text-sm text-gray-600 max-w-md mx-auto">
              Esta sección está actualmente en desarrollo. Pronto podrás configurar opciones
              generales de la aplicación aquí.
            </p>
          </div>
        </div>
      </div>

      <!-- Panel de Seguridad -->
      <div
        v-else-if="currentTab === 'security'"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-medium text-gray-800">Configuración de seguridad</h2>
          <p class="mt-1 text-sm text-gray-500">Gestiona las opciones de seguridad de tu cuenta.</p>
        </div>

        <div class="p-6 space-y-6">
          <div class="text-center py-8">
            <SecurityIcon class="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 class="text-lg font-medium text-gray-800 mb-2">Configuración en desarrollo</h3>
            <p class="text-sm text-gray-600 max-w-md mx-auto">
              Esta sección está actualmente en desarrollo. Pronto podrás configurar opciones de
              seguridad aquí.
            </p>
          </div>
        </div>
      </div>
    </template>
  </TabLayout>
</template>
